import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { Percent, Link2, BarChart3, Wallet, Award } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { brand } from '@/config/brand';
import { useRegisterAffiliate } from '@/api/affiliate';
import { errorMessage } from '@/api/error-message';
import styles from './Affiliate.module.css';

const BENEFITS = [
  { icon: Percent, text: 'Competitive commission on every referral' },
  { icon: Link2, text: 'Your own unique referral link' },
  { icon: BarChart3, text: 'Real-time clicks, conversions and revenue' },
  { icon: Wallet, text: 'Easy payouts to bank or wallet' },
  { icon: Award, text: 'Tier rewards as you grow' },
];

export function RegisterAffiliate() {
  const { user } = useAuth();
  const { show } = useToast();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState<string | null>(null);

  const register = useRegisterAffiliate({
    onSuccess: () => show({ tone: 'success', title: 'Welcome to the affiliate program!' }),
    onError: (e) =>
      show({
        tone: 'danger',
        title: 'Could not join the program',
        description: errorMessage(e, 'Please try again.'),
      }),
  });

  function handleSubmit() {
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setError(null);
    register.mutate({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      company: company.trim() || undefined,
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Join the affiliate program</CardTitle>
        <CardDescription>
          Earn commission by referring customers to {brand.shortName}.
        </CardDescription>
      </CardHeader>
      <CardBody>
        <div className={styles.benefits}>
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <span key={b.text} className={styles.benefit}>
                <Icon size={16} /> {b.text}
              </span>
            );
          })}
        </div>

        <form
          id="register-affiliate-form"
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Field
            label="Full name"
            required
            error={!!error && !name.trim() ? 'Name is required.' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                value={name}
                invalid={!!error && !name.trim()}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Email"
            required
            error={!!error && !email.trim() ? 'Email is required.' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                invalid={!!error && !email.trim()}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Phone">
            {(props) => <Input {...props} value={phone} onChange={(e) => setPhone(e.target.value)} />}
          </Field>
          <Field label="Company">
            {(props) => (
              <Input {...props} value={company} onChange={(e) => setCompany(e.target.value)} />
            )}
          </Field>
        </form>
      </CardBody>
      <CardFooter>
        <Button
          type="submit"
          form="register-affiliate-form"
          disabled={!name.trim() || !email.trim() || register.isPending}
        >
          {register.isPending ? 'Joining…' : 'Join program'}
        </Button>
      </CardFooter>
    </Card>
  );
}
